import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllGuestPlayers, linkGuestToUser, GuestPlayer } from '../services/userManagement';
import './LinkGuestModal.css';

interface LinkGuestModalProps {
  userUuid: string;
  userName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const LinkGuestModal: React.FC<LinkGuestModalProps> = ({
  userUuid,
  userName,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [guestPlayers, setGuestPlayers] = useState<GuestPlayer[]>([]);
  const [selectedGuestId, setSelectedGuestId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadGuestPlayers();
  }, []);

  const loadGuestPlayers = async () => {
    try {
      setLoading(true);
      const guests = await getAllGuestPlayers();
      setGuestPlayers(guests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guest players');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    if (!selectedGuestId) {
      setError('Please select a guest player');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const result = await linkGuestToUser(selectedGuestId, userUuid);

      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Failed to link guest player');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link guest player');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredGuests = guestPlayers.filter(guest => {
    const fullName = `${guest.preName} ${guest.name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content link-guest-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('admin.linkGuestPlayer')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="user-info">
            {t('admin.linkingToUser')}: <strong>{userName}</strong>
          </p>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="search-section">
            <input
              type="text"
              className="guest-search-input"
              placeholder={t('admin.searchGuestPlayers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading-state">{t('common.loading')}</div>
          ) : (
            <div className="guest-list">
              {filteredGuests.length === 0 ? (
                <div className="no-guests">
                  {searchTerm ? t('admin.noGuestsFound') : t('admin.noGuestPlayers')}
                </div>
              ) : (
                filteredGuests.map(guest => (
                  <div
                    key={guest.id}
                    className={`guest-item ${selectedGuestId === guest.id ? 'selected' : ''}`}
                    onClick={() => setSelectedGuestId(guest.id)}
                  >
                    <div className="guest-info">
                      <div className="guest-name">
                        {guest.preName} {guest.name}
                      </div>
                      <div className="guest-meta">
                        <span className="guest-badge">Guest</span>
                        <span className="guest-games">
                          {guest.total_games || 0} {t('admin.games')}
                        </span>
                        {guest.pin && (
                          <span className="guest-pin">PIN: {guest.pin}</span>
                        )}
                      </div>
                    </div>
                    <div className="selection-indicator">
                      {selectedGuestId === guest.id && '✓'}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleLink}
            disabled={!selectedGuestId || submitting}
          >
            {submitting ? t('common.saving') : t('admin.linkPlayer')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkGuestModal;

